import {
  isQuestionScored,
  migrateVisibleIf,
  wellFormedRules,
  type ConditionRule,
  type Question,
  type QuestionType,
  type SampleField,
  type SampleFieldType,
  type Section,
  type TemplateDefinition,
} from './template';

/**
 * Pure, immutable edits for the Template Builder (Phase 5b). Every function
 * returns a NEW definition; nothing here touches SharePoint. Ids are
 * generated once and never change when text is edited — AuditResponses are
 * keyed by question id, so keeping ids stable across versions keeps the
 * history comparable (Phase 6 analytics).
 */

export const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  yes_no: 'Yes / No',
  yes_no_na: 'Yes / No / N/A',
  short_text: 'Short text',
  long_text: 'Long text',
  number: 'Number',
  percent: 'Percent',
  date: 'Date',
  multi_select: 'Multiple choice (pick any)',
  sample_group: 'Sample group (per unit)',
};

/**
 * Types an ordinary question can switch between. A sample group is chosen
 * only when the question is ADDED and can't be converted either way: its
 * answers are a list of entries, so a switch would leave the same question
 * id meaning two incompatible things across versions.
 */
export const CREATABLE_TYPES: QuestionType[] = [
  'yes_no',
  'yes_no_na',
  'short_text',
  'long_text',
  'number',
  'percent',
  'date',
  'multi_select',
];

/** Types offered when adding a question. */
export const ADDABLE_TYPES: QuestionType[] = [...CREATABLE_TYPES, 'sample_group'];

/** Starting size of a new sample group ("verify up to 5"). */
export const NEW_SAMPLE_COUNT = 5;

export const SAMPLE_FIELD_TYPE_LABEL: Record<SampleFieldType, string> = {
  short_text: 'Short text',
  long_text: 'Long text',
  yes_no: 'Yes / No',
  yes_no_na: 'Yes / No / N/A',
};

/** Only yes/no answers feed the score (scoring.ts), so only they can be scored. */
export const isYesNoType = (t: QuestionType) => t === 'yes_no' || t === 'yes_no_na';

/** Every section, question and condition id in the template. */
export function allIds(def: TemplateDefinition): Set<string> {
  const ids = new Set<string>();
  for (const s of def.sections) {
    ids.add(s.id);
    for (const q of s.questions) ids.add(q.id);
  }
  for (const r of def.conditions ?? []) ids.add(r.id);
  return ids;
}

/**
 * A draft's legacy "Show this question" conditions as Show rules on the
 * Conditions page (owner ruling 2026-11-02: when the next draft is opened —
 * on "New draft" and when the builder loads an older draft). Pure.
 */
export function migrateDraftConditions(def: TemplateDefinition): TemplateDefinition {
  // The builder only ever holds well-formed rules (a hand-edited broken one is
  // dropped here — publish would refuse it anyway).
  if (def.conditions !== undefined) {
    const rules = wellFormedRules(def.conditions);
    const same = Array.isArray(def.conditions) && rules.length === def.conditions.length && rules.every((r, i) => JSON.stringify(r) === JSON.stringify((def.conditions as unknown[])[i]));
    if (!same) def = withRules(def, rules);
  }
  const taken = allIds(def);
  return migrateVisibleIf(def, () => {
    const id = newId('c', taken);
    taken.add(id);
    return id;
  });
}

/**
 * A fresh id that has never meant anything else: `${prefix}_` + 8 random
 * base-36 chars. Text-derived ids would come back after a delete (an empty
 * new question is always "q_item"), so a NEW question in v2 could inherit
 * the id — and in Phase 6 the answer history — of a deleted v1 question.
 */
export function newId(prefix: 'q' | 's' | 'f' | 'c', taken: ReadonlySet<string>): string {
  for (;;) {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    const id = `${prefix}_${[...bytes].map((b) => (b % 36).toString(36)).join('')}`;
    if (!taken.has(id)) return id;
  }
}

function move<T>(items: readonly T[], from: number, to: number): T[] {
  const copy = [...items];
  if (from < 0 || from >= copy.length || to < 0 || to >= copy.length || from === to) return copy;
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item!);
  return copy;
}

/** A patch may set an optional setting to undefined (= remove it). */
export type Patch<T> = { [K in keyof T]?: T[K] | undefined };

const withSections = (def: TemplateDefinition, sections: Section[]): TemplateDefinition => ({
  ...def,
  sections,
});

// --- Sections --------------------------------------------------------------

export function addSection(def: TemplateDefinition, title: string): TemplateDefinition {
  const id = newId('s', allIds(def));
  return withSections(def, [...def.sections, { id, title, questions: [] }]);
}

export function updateSection(
  def: TemplateDefinition,
  sectionIndex: number,
  patch: Patch<Omit<Section, 'id' | 'questions'>>,
): TemplateDefinition {
  return withSections(
    def,
    def.sections.map((s, i) =>
      i === sectionIndex ? (clean({ ...s, ...blankToUndefined(patch) }) as Section) : s,
    ),
  );
}

/**
 * Delete a section and its questions. Anything elsewhere that was shown
 * only when one of those questions was answered loses that condition
 * (always shows) — otherwise the template is left pointing at a question
 * that no longer exists.
 */
export function removeSection(def: TemplateDefinition, sectionIndex: number): TemplateDefinition {
  const section = def.sections[sectionIndex];
  const gone = section?.questions.map((q) => q.id) ?? [];
  const cleared = dropRuleTargets(
    gone.reduce((d, id) => clearConditionsOn(d, id), def),
    new Set(section ? [section.id, ...gone] : []),
  );
  return withSections(cleared, cleared.sections.filter((_, i) => i !== sectionIndex));
}

/** Items OUTSIDE section `sectionIndex` that depend on one of its questions. */
export function sectionDependents(def: TemplateDefinition, sectionIndex: number): string[] {
  const section = def.sections[sectionIndex];
  if (!section) return [];
  const inside = new Set(section.questions.map((q) => q.id));
  const names: string[] = [];
  def.sections.forEach((s, i) => {
    if (i === sectionIndex) return;
    if (s.visibleIf && inside.has(s.visibleIf.questionId)) names.push(`section “${s.title}”`);
    for (const q of s.questions) {
      if (q.visibleIf && inside.has(q.visibleIf.questionId)) names.push(`“${q.text}”`);
    }
  });
  (def.conditions ?? []).forEach((r, i) => {
    if (inside.has(r.when.questionId)) names.push(`condition ${i + 1}`);
  });
  return names;
}

export function moveSection(def: TemplateDefinition, from: number, to: number): TemplateDefinition {
  return withSections(def, move(def.sections, from, to));
}

// --- Questions -------------------------------------------------------------

export function addQuestion(
  def: TemplateDefinition,
  sectionIndex: number,
  type: QuestionType,
  text: string,
  /** A no-score template: a new Yes/No question starts as not counting toward a score. */
  opts: { unscored?: boolean } = {},
): TemplateDefinition {
  const id = newId('q', allIds(def));
  const q: Question = {
    id,
    type,
    text,
    ...(opts.unscored && (type === 'yes_no' || type === 'yes_no_na') ? { scored: false } : {}),
    ...(type === 'multi_select' ? { options: [] } : {}),
    ...(type === 'sample_group' ? { sampleCount: NEW_SAMPLE_COUNT, fields: [] } : {}),
  };
  return withSections(
    def,
    def.sections.map((s, i) => (i === sectionIndex ? { ...s, questions: [...s.questions, q] } : s)),
  );
}

/** Drop keys set to undefined so the stored JSON stays minimal. */
function clean<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

/** Optional text settings: an emptied box removes the setting (text/title stay, even if empty). */
const OPTIONAL_TEXT = new Set(['heading', 'subsection', 'instruction']);
function blankToUndefined<T extends object>(patch: T): T {
  return Object.fromEntries(
    Object.entries(patch).map(([k, v]) => [k, OPTIONAL_TEXT.has(k) && v === '' ? undefined : v]),
  ) as T;
}

export function updateQuestion(
  def: TemplateDefinition,
  sectionIndex: number,
  questionIndex: number,
  patch: Patch<Omit<Question, 'id'>>,
): TemplateDefinition {
  return withSections(
    def,
    def.sections.map((s, i) =>
      i !== sectionIndex
        ? s
        : {
            ...s,
            questions: s.questions.map((q, j) =>
              j === questionIndex ? normalizeQuestion(clean({ ...q, ...blankToUndefined(patch) } as Question)) : q,
            ),
          },
    ),
  );
}

/**
 * Remove settings the question's type can't use, so changing a type never
 * leaves a template that fails validation for a reason the editor doesn't
 * show (e.g. options on a yes/no question, a weight on a text question).
 */
export function normalizeQuestion(q: Question): Question {
  const next: Question = { ...q };
  if (next.type !== 'multi_select') delete next.options;
  if (next.type !== 'sample_group') {
    delete next.sampleCount;
    delete next.minCount;
    delete next.fields;
  }
  if (next.type !== 'yes_no') delete next.prefillStoreAttribute;
  if (next.type !== 'number' && next.type !== 'percent') delete next.budget;
  if (!isYesNoType(next.type) && next.type !== 'sample_group') {
    delete next.scored;
    delete next.weight;
    delete next.critical;
  }
  // isQuestionScored, not `scored === false`: a sample group's scoring
  // comes from its fields. A group keeps its weight and critical flag even
  // while none of its fields counts (scoring ignores them then): unticking
  // and re-ticking a field must not quietly reset the weight to 1.
  if (!isQuestionScored(next)) {
    if (next.type !== 'sample_group') {
      delete next.weight;
      delete next.critical;
    }
    // "Required when the answer fails" can never fire on an unscored question.
    if (next.attachments === 'required_on_fail') delete next.attachments;
  }
  if (next.critical === false) delete next.critical;
  if (next.required === true) delete next.required; // true is the default
  if (next.attachments === 'none') delete next.attachmentTypes;
  return next;
}

export function removeQuestion(
  def: TemplateDefinition,
  sectionIndex: number,
  questionIndex: number,
): TemplateDefinition {
  const gone = def.sections[sectionIndex]?.questions[questionIndex]?.id;
  const base = gone ? dropRuleTargets(def, new Set([gone])) : def;
  return withSections(
    base,
    base.sections.map((s, i) => {
      if (i !== sectionIndex) return s;
      const removed = s.questions[questionIndex];
      const questions = s.questions.filter((_, j) => j !== questionIndex);
      // The first question of a subsection hands its title to the next one, so
      // deleting it doesn't silently merge the rest into the subsection above.
      const next = questions[questionIndex];
      const title = typeof removed?.subsection === 'string' ? removed.subsection.trim() : '';
      if (title && next && !(typeof next.subsection === 'string' && next.subsection.trim())) {
        questions[questionIndex] = { ...next, subsection: title };
      }
      return { ...s, questions };
    }),
  );
}

/** Move a question within its section or into another section (at `toIndex`). */
export function moveQuestion(
  def: TemplateDefinition,
  fromSection: number,
  fromIndex: number,
  toSection: number,
  toIndex: number,
): TemplateDefinition {
  const q = def.sections[fromSection]?.questions[fromIndex];
  if (!q || !def.sections[toSection]) return def;
  if (fromSection === toSection) {
    return withSections(
      def,
      def.sections.map((s, i) =>
        i === fromSection ? { ...s, questions: move(s.questions, fromIndex, toIndex) } : s,
      ),
    );
  }
  return withSections(
    def,
    def.sections.map((s, i) => {
      if (i === fromSection) return { ...s, questions: s.questions.filter((_, j) => j !== fromIndex) };
      if (i === toSection) {
        const at = Math.max(0, Math.min(toIndex, s.questions.length));
        return { ...s, questions: [...s.questions.slice(0, at), q, ...s.questions.slice(at)] };
      }
      return s;
    }),
  );
}

/**
 * Drag-and-drop target index: dropping question `fromIndex` of section
 * `fromSection` onto the slot BEFORE question `beforeIndex` of section
 * `toSection` (beforeIndex = length means "at the end"). Moving down within
 * the same section shifts the target by one, because removing the dragged
 * question first moves everything after it up.
 */
export function dropIndex(fromSection: number, fromIndex: number, toSection: number, beforeIndex: number): number {
  return fromSection === toSection && fromIndex < beforeIndex ? beforeIndex - 1 : beforeIndex;
}

/**
 * Bulk weight edit: set `weight` on every listed question (unscored ones
 * are skipped by normalizeQuestion's rules — a weight only sticks where it
 * counts). weight 1 is the default, so it is stored as "no weight".
 */
export function setWeights(
  def: TemplateDefinition,
  questionIds: ReadonlySet<string>,
  weight: number,
): TemplateDefinition {
  return withSections(
    def,
    def.sections.map((s) => ({
      ...s,
      questions: s.questions.map((q) => {
        if (!questionIds.has(q.id) || !isQuestionScored(q)) return q;
        const next: Question = { ...q, weight };
        if (weight === 1) delete next.weight;
        return normalizeQuestion(next);
      }),
    })),
  );
}

/** Sections and questions whose legacy gate, or any condition rule, depends on this question. */
export function dependentsOf(def: TemplateDefinition, questionId: string): string[] {
  const names: string[] = [];
  for (const s of def.sections) {
    if (s.visibleIf?.questionId === questionId) names.push(`section “${s.title}”`);
    for (const q of s.questions) {
      if (q.visibleIf?.questionId === questionId) names.push(`“${q.text}”`);
    }
  }
  (def.conditions ?? []).forEach((r, i) => {
    if (r.when.questionId === questionId) names.push(`condition ${i + 1}`);
  });
  return names;
}

/** Rules that apply to `id` and nothing else — deleting it deletes them (dropRuleTargets; UI review 3, A12). */
export function rulesOnlyTargeting(def: TemplateDefinition, id: string): string[] {
  const out: string[] = [];
  (def.conditions ?? []).forEach((r, i) => {
    if (r.targets.length > 0 && r.targets.every((t) => t === id)) out.push(`condition ${i + 1}`);
  });
  return out;
}

/** Remove every condition (legacy or rule) that `questionId` triggers. */
export function clearConditionsOn(def: TemplateDefinition, questionId: string): TemplateDefinition {
  const rules = def.conditions?.filter((r) => r.when.questionId !== questionId);
  return withSections(
    rules && rules.length !== def.conditions!.length ? withRules(def, rules) : def,
    def.sections.map((s) => {
      const section: Section = { ...s };
      if (section.visibleIf?.questionId === questionId) delete section.visibleIf;
      section.questions = s.questions.map((q) => {
        if (q.visibleIf?.questionId !== questionId) return q;
        const next: Question = { ...q };
        delete next.visibleIf;
        return next;
      });
      return section;
    }),
  );
}

/**
 * Question text by id, for readable condition labels and error messages.
 * Sample fields are keyed `questionId.fieldId`, the form validateTemplate
 * names them in.
 */
export function labelsById(def: TemplateDefinition): Map<string, string> {
  const labels = new Map<string, string>();
  def.sections.forEach((s, si) => {
    labels.set(s.id, `section ${si + 1} “${s.title || 'untitled'}”`);
    s.questions.forEach((q, qi) => {
      const full = q.text ?? '';
      const text = full.length > 50 ? `${full.slice(0, 50)}…` : full;
      labels.set(q.id, `question ${si + 1}.${qi + 1} “${text || 'untitled'}”`);
      (q.fields ?? []).forEach((f, fi) => {
        labels.set(`${q.id}.${f.id}`, `question ${si + 1}.${qi + 1}, field ${fi + 1} “${f.label || 'untitled'}”`);
      });
    });
  });
  (Array.isArray(def.conditions) ? def.conditions : []).forEach((r, i) => {
    if (typeof r?.id === 'string') labels.set(r.id, `condition ${i + 1}`);
  });
  return labels;
}

/**
 * validateTemplate speaks in ids ("question 'q_x' weight must be > 0");
 * swap each quoted id for its section/question label so an admin can find it.
 */
export function friendlyErrors(def: TemplateDefinition, errors: readonly string[]): string[] {
  const labels = labelsById(def);
  // One pass over the ORIGINAL text, so an apostrophe inside a label we
  // already substituted can't derail the next match.
  return errors.map((e) => {
    const text = e.replace(
      /(?:(?:condition|section|question|multi_select|sample_group|sample field) )?'([^']+)'/g,
      (m, id: string) => labels.get(id) ?? m,
    );
    // A sentence, not a log line (UI review 3, A10: "condition 4 applies to …").
    return text.charAt(0).toUpperCase() + text.slice(1);
  });
}

/**
 * Worth a look before publishing — never blocks (owner ruling 2026-11-03, UI
 * review 3 A2): a Yes/No question that counts toward the score AND decides a
 * condition usually records a fact ("Does this store have elevators?"), and
 * its "No" would count as a failed item. `owner` = the question, for "Go to it".
 */
export function scoredTriggerWarnings(def: TemplateDefinition): { text: string; owner: string }[] {
  const out: { text: string; owner: string }[] = [];
  const seen = new Set<string>();
  // Rules, and old show-if gates not yet converted (a draft published without being opened — QA).
  const triggers = [
    ...(def.conditions ?? []).map((r) => r.when?.questionId),
    ...def.sections.flatMap((s) => [s.visibleIf?.questionId, ...s.questions.map((q) => q.visibleIf?.questionId)]),
  ];
  for (const id of triggers) {
    if (!id || seen.has(id)) continue;
    const q = def.sections.flatMap((s) => s.questions).find((x) => x.id === id);
    if (!q || (q.type !== 'yes_no' && q.type !== 'yes_no_na') || !isQuestionScored(q)) continue;
    seen.add(id);
    out.push({
      owner: id,
      text: `“${q.text.trim() || 'Untitled question'}” decides a condition and counts toward the score, so a “No” counts as a failed item. If it only records a fact, untick “Counts toward the score”.`,
    });
  }
  return out;
}

// --- Sample-group fields (5c) ---------------------------------------------

const isYesNoField = (t: SampleFieldType) => t === 'yes_no' || t === 'yes_no_na';

/** Apply `fn` to one question; everything else is returned untouched. */
function mapQuestion(
  def: TemplateDefinition,
  sectionIndex: number,
  questionIndex: number,
  fn: (q: Question) => Question,
): TemplateDefinition {
  return withSections(
    def,
    def.sections.map((s, i) =>
      i !== sectionIndex ? s : { ...s, questions: s.questions.map((q, j) => (j === questionIndex ? fn(q) : q)) },
    ),
  );
}

/**
 * Drop field settings that can no longer apply, so an edit never leaves a
 * rule the editor doesn't show: `scored` only on yes/no fields (and only
 * when false — true is the default), and any "required when" that now
 * points at a missing field, a non-yes/no field, itself, or an answer
 * ("N/A") its target can't give.
 */
function normalizeFields(fields: readonly SampleField[]): SampleField[] {
  const byId = new Map(fields.map((f) => [f.id, f]));
  return fields.map((f) => {
    const next: SampleField = { ...f };
    if (!isYesNoField(next.type) || next.scored !== false) delete next.scored;
    const target = next.requiredIf ? byId.get(next.requiredIf.fieldId) : undefined;
    if (
      next.requiredIf &&
      (!target ||
        target.id === next.id ||
        !isYesNoField(target.type) ||
        (next.requiredIf.equals === 'na' && target.type !== 'yes_no_na'))
    ) {
      delete next.requiredIf;
    }
    return next;
  });
}

export function addSampleField(
  def: TemplateDefinition,
  sectionIndex: number,
  questionIndex: number,
  type: SampleFieldType,
  /** A no-score template: a new Yes/No field starts as not counting toward a score. */
  opts: { unscored?: boolean } = {},
): TemplateDefinition {
  return mapQuestion(def, sectionIndex, questionIndex, (q) => {
    const fields = q.fields ?? [];
    const id = newId('f', new Set(fields.map((f) => f.id)));
    const unscored = opts.unscored && (type === 'yes_no' || type === 'yes_no_na') ? { scored: false } : {};
    return { ...q, fields: [...fields, { id, label: '', type, ...unscored }] };
  });
}

export function updateSampleField(
  def: TemplateDefinition,
  sectionIndex: number,
  questionIndex: number,
  fieldIndex: number,
  patch: Patch<Omit<SampleField, 'id'>>,
): TemplateDefinition {
  return mapQuestion(def, sectionIndex, questionIndex, (q) => ({
    ...q,
    fields: normalizeFields(
      (q.fields ?? []).map((f, i) => (i === fieldIndex ? clean({ ...f, ...patch } as SampleField) : f)),
    ),
  }));
}

/** Removing a field also drops any "required when" on its siblings that pointed at it. */
export function removeSampleField(
  def: TemplateDefinition,
  sectionIndex: number,
  questionIndex: number,
  fieldIndex: number,
): TemplateDefinition {
  return mapQuestion(def, sectionIndex, questionIndex, (q) => ({
    ...q,
    fields: normalizeFields((q.fields ?? []).filter((_, i) => i !== fieldIndex)),
  }));
}

export function moveSampleField(
  def: TemplateDefinition,
  sectionIndex: number,
  questionIndex: number,
  from: number,
  to: number,
): TemplateDefinition {
  return mapQuestion(def, sectionIndex, questionIndex, (q) => ({ ...q, fields: move(q.fields ?? [], from, to) }));
}

/** Labels of sibling fields that are "required when" `fieldId` has some answer. */
export function sampleFieldDependents(q: Question, fieldId: string): string[] {
  return (q.fields ?? [])
    .filter((f) => f.requiredIf?.fieldId === fieldId)
    .map((f) => `“${f.label || 'untitled field'}”`);
}

// --- Conditions page (owner rulings 2026-11-02) ------------------------------

/** Question types that can trigger a rule ("anything that is a single selection"). */
export const isTriggerType = (t: QuestionType) => t === 'yes_no' || t === 'yes_no_na' || t === 'multi_select';
/** Which kind of trigger a type makes — changing kind invalidates its rules. */
export const triggerKind = (t: QuestionType) => (t === 'multi_select' ? 'choice' : isTriggerType(t) ? 'answer' : null);

/** A word-boundary cut for a long question in a picker or chip. */
function shortText(text: string, max = 60): string {
  const t = text.trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:.–—-]+$/, '')}…`;
}

/**
 * How a rule names a question or section in its pickers and chips: "2.4 Are
 * all parked vehicles registered…" / "Whole section 9: Elevator inspection"
 * (the picker's group already names the section). `full` = the whole text.
 */
export function ruleItemLabel(def: TemplateDefinition, id: string): { label: string; full: string } | null {
  for (const [si, s] of def.sections.entries()) {
    if (s.id === id) {
      const title = s.title || 'Untitled section';
      return { label: `Whole section ${si + 1}: ${title}`, full: `Whole section ${si + 1}: ${title}` };
    }
    const qi = s.questions.findIndex((q) => q.id === id);
    if (qi >= 0) {
      const text = s.questions[qi]!.text || 'Untitled question';
      return { label: `${si + 1}.${qi + 1} ${shortText(text)}`, full: `${si + 1}.${qi + 1} ${text}` };
    }
  }
  return null;
}

export const RULE_ACTION_LABEL: Record<ConditionRule['action'], string> = {
  show: 'Show',
  hide: 'Hide',
  require: 'Require',
  not_require: 'Not require',
};

function withRules(def: TemplateDefinition, rules: ConditionRule[]): TemplateDefinition {
  const next: TemplateDefinition = { ...def, conditions: rules };
  if (rules.length === 0) delete next.conditions;
  return next;
}

/** Take deleted items out of every rule; a rule left with no targets goes too. */
export function dropRuleTargets(def: TemplateDefinition, ids: ReadonlySet<string>): TemplateDefinition {
  if (!def.conditions?.length || ids.size === 0) return def;
  let changed = false;
  const rules: ConditionRule[] = [];
  for (const r of def.conditions) {
    const targets = r.targets.filter((t) => !ids.has(t));
    if (targets.length === r.targets.length) rules.push(r);
    else {
      changed = true;
      if (targets.length > 0) rules.push({ ...r, targets });
    }
  }
  return changed ? withRules(def, rules) : def;
}

export function addRule(def: TemplateDefinition): TemplateDefinition {
  const rule: ConditionRule = { id: newId('c', allIds(def)), action: 'show', when: { questionId: '', op: 'is', value: '' }, targets: [] };
  return withRules(def, [...(def.conditions ?? []), rule]);
}

export function updateRule(def: TemplateDefinition, index: number, next: ConditionRule): TemplateDefinition {
  return withRules(def, (def.conditions ?? []).map((r, i) => (i === index ? next : r)));
}

export function removeRule(def: TemplateDefinition, index: number): TemplateDefinition {
  return withRules(def, (def.conditions ?? []).filter((_, i) => i !== index));
}

/**
 * A rule with a new trigger question: the check resets to that question's
 * first answer, and targets that would now come before it are dropped.
 */
export function setRuleTrigger(def: TemplateDefinition, rule: ConditionRule, questionId: string): ConditionRule {
  const q = def.sections.flatMap((s) => s.questions).find((x) => x.id === questionId);
  const when: ConditionRule['when'] =
    q?.type === 'multi_select' ? { questionId, op: 'includes', value: q.options?.[0] ?? '' } : { questionId, op: 'is', value: q ? 'yes' : '' };
  const allowed = new Set(targetOptions(def, { ...rule, when }).map((o) => o.id));
  return { ...rule, when, targets: rule.targets.filter((t) => allowed.has(t)) };
}

/** Ids shown only sometimes: a legacy visibleIf or a Show / Hide target (they can't trigger). */
function sometimesShown(def: TemplateDefinition): Set<string> {
  const ids = new Set<string>();
  for (const s of def.sections) {
    if (s.visibleIf) ids.add(s.id);
    for (const q of s.questions) if (q.visibleIf) ids.add(q.id);
  }
  for (const r of def.conditions ?? []) if (r.action === 'show' || r.action === 'hide') r.targets.forEach((t) => ids.add(t));
  return ids;
}

export interface RuleOption {
  id: string;
  label: string;
  kind: 'question' | 'section';
  /** The section it sits in (for grouping). */
  group: string;
}

/**
 * Questions that may trigger a rule: Yes/No, Yes/No/N/A or multi-select,
 * and always shown (not itself the target of a Show / Hide rule — no
 * chained gates). Mirrors validateTemplate.
 */
export function triggerOptions(def: TemplateDefinition): RuleOption[] {
  const sometimes = sometimesShown(def);
  return def.sections.flatMap((s, si) =>
    s.questions
      .filter((q) => isTriggerType(q.type) && !sometimes.has(q.id))
      .map((q) => ({ id: q.id, label: ruleItemLabel(def, q.id)?.label ?? q.id, kind: 'question' as const, group: `${si + 1}. ${s.title || 'Untitled section'}` })),
  );
}

/**
 * What a rule may apply to: questions AFTER its trigger (and, for Show /
 * Hide, sections after the trigger's section) — never a question that
 * triggers other rules for Show / Hide (it would become a chained gate).
 */
export function targetOptions(def: TemplateDefinition, rule: ConditionRule): RuleOption[] {
  const label = (id: string) => ruleItemLabel(def, id)?.label ?? id;
  let si0 = -1;
  let qi0 = -1;
  def.sections.forEach((s, si) => s.questions.forEach((q, qi) => {
    if (q.id === rule.when.questionId) [si0, qi0] = [si, qi];
  }));
  if (si0 < 0) return [];
  const visibility = rule.action === 'show' || rule.action === 'hide';
  const others = (def.conditions ?? []).filter((r) => r.id !== rule.id);
  const triggers = new Set(others.map((r) => r.when.questionId));
  // A trigger in a section shown only sometimes may control its own section only (validateTemplate).
  const confined = sometimesShown(def).has(def.sections[si0]!.id);
  const out: RuleOption[] = [];
  def.sections.forEach((s, si) => {
    if (si < si0 || (confined && si > si0)) return;
    // Hiding a whole section that holds another rule's trigger would make it a chained gate.
    const holdsTrigger = s.questions.some((q) => triggers.has(q.id));
    const group = `${si + 1}. ${s.title || 'Untitled section'}`;
    if (visibility && si > si0 && !holdsTrigger) out.push({ id: s.id, label: label(s.id), kind: 'section', group });
    s.questions.forEach((q, qi) => {
      if (si === si0 && qi <= qi0) return;
      if (visibility && triggers.has(q.id)) return;
      out.push({ id: q.id, label: label(q.id), kind: 'question', group });
    });
  });
  return out;
}

/** "2.3" for a question, "section 2" for a section. */
export function itemNumber(def: TemplateDefinition, id: string): string {
  for (const [si, s] of def.sections.entries()) {
    if (s.id === id) return `section ${si + 1}`;
    const qi = s.questions.findIndex((q) => q.id === id);
    if (qi >= 0) return `${si + 1}.${qi + 1}`;
  }
  return 'a removed question';
}

const ANSWER_LABEL: Record<string, string> = { yes: 'Yes', no: 'No', na: 'N/A' };

/** "2.2 is Yes", "3.1 includes “Snow removal”", "3.1 does not include “Gate”". */
export function triggerText(def: TemplateDefinition, when: ConditionRule['when']): string {
  const at = itemNumber(def, when.questionId);
  if (when.op === 'is') return `${at} is ${ANSWER_LABEL[when.value] ?? '…'}`;
  return `${at} ${when.op === 'includes' ? 'includes' : 'does not include'} “${when.value || '…'}”`;
}

/**
 * The read-only lines a question or section shows in the Questions view
 * ("Shown only when 2.2 is Yes — edit in Conditions"), rule order. A legacy
 * visibleIf (a draft not yet converted) reads as a Show rule.
 */
export function conditionLines(def: TemplateDefinition, id: string): string[] {
  const own = def.sections.flatMap((s) => [s, ...s.questions]).find((x) => x.id === id);
  const shows: string[] = [];
  const lines: string[] = [];
  if (own?.visibleIf) shows.push(triggerText(def, { questionId: own.visibleIf.questionId, op: 'is', value: own.visibleIf.equals }));
  for (const r of def.conditions ?? []) {
    if (!r.targets.includes(id) || !r.when.questionId) continue;
    const t = triggerText(def, r.when);
    if (r.action === 'show') shows.push(t);
    else lines.push(`${r.action === 'hide' ? 'Hidden' : r.action === 'require' ? 'Required' : 'Not required'} when ${t}`);
  }
  if (shows.length) lines.unshift(`Shown only when ${shows.join(', or when ')}`);
  return lines;
}

/**
 * Keep rules in step with an edit to their trigger question: a renamed
 * multiple-choice option renames the checked value (same position, new
 * text); a removed option — or N/A when Yes/No/N/A becomes Yes/No — leaves
 * the rules that check for it pointing at nothing, so they are dropped and
 * named for the confirm. Type changes of KIND are handled by the caller.
 */
export function syncRulesToQuestion(
  def: TemplateDefinition,
  q: Question,
  patch: Patch<Omit<Question, 'id'>>,
): { def: TemplateDefinition; dropped: string[] } {
  const rules = def.conditions ?? [];
  if (!rules.some((r) => r.when.questionId === q.id)) return { def, dropped: [] };
  let keep: (r: ConditionRule) => ConditionRule | null = (r) => r;
  if (q.type === 'multi_select' && patch.options && (patch.type === undefined || patch.type === 'multi_select')) {
    const before = q.options ?? [];
    const after = patch.options;
    const renamed = new Map<string, string>();
    if (before.length === after.length) before.forEach((o, i) => o !== after[i] && renamed.set(o, after[i]!));
    keep = (r) => {
      if (r.when.questionId !== q.id || after.includes(r.when.value)) return r;
      const to = renamed.get(r.when.value);
      return to !== undefined && to.trim() ? { ...r, when: { ...r.when, value: to } } : before.includes(r.when.value) ? null : r;
    };
  } else if (q.type === 'yes_no_na' && patch.type === 'yes_no') {
    keep = (r) => (r.when.questionId === q.id && r.when.value === 'na' ? null : r);
  } else {
    return { def, dropped: [] };
  }
  const dropped: string[] = [];
  let changed = false;
  const next: ConditionRule[] = [];
  rules.forEach((r, i) => {
    const k = keep(r);
    if (k !== r) changed = true;
    if (k) next.push(k);
    else dropped.push(`condition ${i + 1}`);
  });
  return { def: changed ? withRules(def, next) : def, dropped };
}
